import { Injectable, inject } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, take } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import {
  AppleProfileStoreActions,
  DeviceStoreActions,
  DeviceStoreSelectors,
  DevicesRouterStoreSelectors,
} from '@era/stores';

/**
 * Resolves the device detail route by triggering Apple device + profile load, then waiting for the
 * device record to be loaded in the store (from the parent route's locations/devices request)
 * before activating the route. Unassigned and unavailable devices intentionally have no category
 * IDs, so category assignment cannot be used as the readiness condition here.
 */
@Injectable({ providedIn: 'root' })
export class AppleDeviceResolver {
  private readonly store = inject(Store);

  resolve(): Observable<boolean> {
    return this.store.select(DevicesRouterStoreSelectors.selectUdid).pipe(
      first(),
      switchMap((udid) => {
        if (!udid) {
          return of(false);
        }

        // Trigger loading of Apple device and profile data (parent route triggers locations/devices list).
        this.store.dispatch(DeviceStoreActions.DeviceResolverActions.appleDeviceRequested({ udid }));
        this.store.dispatch(AppleProfileStoreActions.AppleDeviceResolverActions.profilesRequested({ udid }));
        // Wait until the device in the store has been loaded by the parent device list request.
        // Category IDs are optional for unassigned and unavailable devices.
        return this.store.select(DeviceStoreSelectors.selectDeviceByUdid(udid)).pipe(
          filter((device) => device?.hasLoadedLocationsDevice === true),
          take(1),
          map(() => true),
          catchError(() => of(true))
        );
      })
    );
  }
}
